"use client";

// "That did not save" — said to the person it happened to, while they can still
// do something about it.
//
// THE FAILURE THIS ANSWERS IS A SILENT ONE, which is what makes the design
// unusual. Nothing has crashed; there is no broken page to replace. The app
// looks exactly right and is quietly lying, and the only way anyone finds out is
// if we interrupt them. So this is a modal, on top of everything, rather than a
// toast that slides away while they are reading something else.
//
// ─── THE THREE THINGS IT HAS TO DO, IN ORDER ───────────────────────────────
//
//   1. SAY IT. Plainly, in the words of the thing they were doing — "your
//      script", "the video’s status" — never "POST /api/store returned 401".
//   2. OFFER ONE CLICK. Most of these are transient: a server restarting mid
//      deploy, a laptop on hotel wifi, a session that expired an hour ago. Try
//      again fixes those and the whole episode is over in two seconds.
//   3. GET OUT OF THE WAY. If it keeps failing, the most valuable thing on
//      screen is the user's own unsaved work, and a modal they cannot close is a
//      modal that stops them copying it somewhere safe. Dismiss collapses to a
//      bar that will not go away until the write lands.
//
// ⚠️ NOTHING HERE DISCARDS ANYTHING. There is deliberately no "revert" button,
// no "reload the page", no offer to sync from the server. Those all end with a
// person watching their own paragraphs disappear as the price of making our two
// copies agree. See the header of lib/write-failure.ts.

import { useCallback, useState, useSyncExternalStore } from "react";
import { AlertTriangle, RefreshCw, X } from "lucide-react";
import {
  getWriteFailures,
  retryWriteFailures,
  subscribeWriteFailures,
  type WriteFailure,
} from "@/lib/write-failure";
import { DialogFrame } from "@/components/dialog-frame";

// useSyncExternalStore calls this during SSR and on the hydration pass. It must
// be a STABLE reference — a fresh [] each call is a new snapshot every render,
// which React reads as "the store changed" forever.
const NONE: WriteFailure[] = [];
const serverSnapshot = () => NONE;

/**
 * The row kinds, in the words of the person who made them.
 *
 * A STORE KIND IS AN IMPLEMENTATION DETAIL and `stage_override` means nothing to
 * a creator. What they need is enough to recognise which of the last few things
 * they did is the one at risk, so the labels name the ACT rather than the table:
 * "the script’s status", not "stage_override".
 *
 * Total by construction — an unmapped kind falls back to "a change", which is
 * vague but never wrong. A Record<string, string> rather than a keyed one on
 * purpose: the kinds are spread across eight stores and adding a ninth should
 * not have to come here to compile.
 */
const KIND_LABELS: Record<string, string> = {
  draft: "your script",
  stage_override: "the script’s status",
  video_stage: "the video’s status",
  stage_undo: "the status history",
  approved_on: "the approval date",
  drive_video: "the video link",
  comment: "your comment",
  grade: "the grade",
  grader_feedback: "your feedback",
  feedback: "your feedback",
  run_comment: "your note",
  rubric_comment: "your note",
  skill_comment: "your note",
  affiliate_skill_comment: "your note",
  client_slack_skill_comment: "your note",
  brand_schedule: "the posting schedule",
  week_date: "the post date",
  week_plan: "the week’s plan",
  week_delete: "deleting the week",
};

const labelFor = (kind: string): string => KIND_LABELS[kind] ?? "a change";

/**
 * Rows that ride along with somebody else's action rather than being one.
 *
 * ONE CLICK IS NOT TWO FAILURES. Approving a script writes three rows — the
 * stage, the undo point, and the date it was approved on — and when the server
 * is refusing writes it refuses all three. The first cut of this box duly
 * reported them all, so a single press of Approve produced "2 changes aren't
 * saved", two reference codes to photograph, and the phrase "the status
 * history", which is a thing no reviewer has ever thought about or should have
 * to start now.
 *
 * So these are filtered out of what the box SAYS. They are not filtered out of
 * anything else: they stay in the queue, they are retried by the same click, and
 * they were each reported to the server on their own reference. The user is told
 * about the thing they did; we keep the bookkeeping.
 *
 * The fallback matters too. If a companion is the ONLY thing that failed — the
 * stage landed and the undo point did not — that is still a real divergence and
 * still gets named, because the alternative is a silent one, which is the whole
 * bug this file exists for.
 */
const COMPANION_KINDS = new Set(["stage_undo", "approved_on"]);

function principal(failures: WriteFailure[]): WriteFailure[] {
  const own = failures.filter((f) => !COMPANION_KINDS.has(f.kind));
  return own.length > 0 ? own : failures;
}

/**
 * What we think went wrong, and what they can do about it.
 *
 * 401 IS SPLIT OUT BECAUSE IT IS THE LIKELY ONE and because it is the only one
 * where Try again alone cannot work. A session that expired an hour ago will
 * refuse the retry exactly as it refused the write, and a person clicking a
 * button that cannot succeed learns to distrust the button. Naming the fix —
 * open a new tab, sign in, come back — is what makes the retry worth pressing.
 */
function diagnosis(failures: WriteFailure[]): string {
  if (failures.some((f) => f.status === 401 || f.status === 403)) {
    return "You may have been signed out. Open the app in a new tab and sign in again, then press Try again here — your work is still on this screen.";
  }
  // 503 IS THE DATABASE, BUSY WITH AN IMPORT for longer than a save may wait
  // (lib/server/user-write.ts: one wait, one retry, then this). It is the case
  // Try again is made for: the import lets go within seconds.
  if (failures.some((f) => f.status === 503)) {
    // Just the sentence: the line above already says the work is still here.
    return "The database is busy; try again.";
  }
  if (failures.some((f) => f.reason === "unreachable")) {
    return "We couldn’t reach the server. Check your connection, then press Try again.";
  }
  // 400 IS THE OTHER ONE TRY AGAIN CANNOT FIX, for the same reason 401 is not:
  // the server refused the SHAPE of the row, so the identical retry is refused
  // identically. Today there is one such rule — a draft with no brand
  // (app/api/store/route.ts) — and the fix is a roster change somebody else
  // makes, which is exactly the kind of thing a generic "press Try again" hides.
  if (failures.some((f) => f.status === 400)) {
    return "The server would not accept this change as it stands — retrying will not help. Your work is still on this screen. Ask an admin to check the creator is assigned to a brand in Settings, then reload.";
  }
  return "The server would not accept the change. Press Try again — if it keeps failing, copy your work somewhere safe and send us the reference below.";
}

/** The distinct THINGS a set of failures is about — one per label, not per row. */
function subjects(failures: WriteFailure[]): string[] {
  return Array.from(new Set(failures.map((f) => labelFor(f.kind))));
}

/** "your script", "your script and the video's status", "a, b and c". */
function subjectList(failures: WriteFailure[]): string {
  const labels = subjects(failures);
  if (labels.length <= 1) return labels[0] ?? "your change";
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

export function WriteFailureNotice() {
  const failures = useSyncExternalStore(subscribeWriteFailures, getWriteFailures, serverSnapshot);
  const [retrying, setRetrying] = useState(false);

  // WHICH FAILURES THEY HAVE ALREADY SEEN THE BOX FOR. Dismiss must silence the
  // ones on screen without silencing the next one — a person who shrugs off a
  // failed autosave and then loses an approval deserves to be interrupted the
  // second time. Keyed by ref, which is unique per filing, so a row that fails
  // again after a retry comes back as a new one.
  const [dismissed, setDismissed] = useState<string[]>([]);

  const unseen = failures.filter((f) => !dismissed.includes(f.ref));
  // What the box talks about, as opposed to what the queue holds — see
  // COMPANION_KINDS. Retry and the report still cover every row.
  const said = principal(unseen);
  const stuck = principal(failures);

  const retry = useCallback(async () => {
    setRetrying(true);
    try {
      await retryWriteFailures();
    } finally {
      // Whatever is left has re-filed itself under a NEW ref, so it is unseen
      // again and the box stays up saying so. Nothing to reset by hand.
      setRetrying(false);
    }
  }, []);

  if (failures.length === 0) return null;

  // ─── Collapsed: dismissed, but never gone ────────────────────────────────
  //
  // The bar is the promise that we have not forgotten. It cannot be closed —
  // only fixed — because the state it describes is one where this tab holds the
  // only copy of something, and a dismissible reminder of that is no reminder.
  if (unseen.length === 0) {
    return (
      <div
        data-testid="write-failure-bar"
        className="fixed bottom-4 left-4 z-[60] flex items-center gap-3 rounded-lg border border-red-500/40 bg-surface px-3 py-2 shadow-lg"
      >
        <AlertTriangle size={14} className="shrink-0 text-red-400" aria-hidden />
        <span className="text-[11.5px] text-fg">
          {stuck.length === 1 ? "1 change isn’t saved" : `${stuck.length} changes aren’t saved`}
        </span>
        <button
          onClick={retry}
          disabled={retrying}
          data-testid="write-failure-bar-retry"
          className="inline-flex items-center gap-1.5 rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-text-muted transition-colors hover:border-line-strong hover:text-fg disabled:opacity-50"
        >
          <RefreshCw size={11} className={retrying ? "animate-spin" : undefined} />
          {retrying ? "Trying…" : "Try again"}
        </button>
      </div>
    );
  }

  // ─── Open: the interruption ──────────────────────────────────────────────
  //
  // Escape and the backdrop do what "Keep working" does: collapse to the bar,
  // never to nothing. Above every other popup (layer), because it can interrupt
  // one — and while it is up, Escape is its alone.
  const dismiss = () => setDismissed(failures.map((f) => f.ref));
  return (
    <DialogFrame
      onClose={dismiss}
      role="alertdialog"
      labelledBy="write-failure-title"
      layer="z-[60]"
      className="max-w-lg rounded-lg border border-red-500/40 bg-surface p-5"
    >
      {/* data-refs, not rendered text: support can read it out of a screenshot's
          DOM or a session recording, and nobody is asked to transcribe it. */}
      <div
        data-testid="write-failure-modal"
        data-refs={said.map((f) => f.ref).join(" ")}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={18} className="mt-px shrink-0 text-red-400" aria-hidden />
            <div>
              {/* Counted in SUBJECTS, not in rows — the same count the sentence
                  under it makes. Renaming a week writes five scripts and two
                  schedule rows, and a heading that called that "some changes"
                  over a sentence reading "we couldn't save your script" was
                  disagreeing with itself about how much had gone wrong. */}
              <h2 id="write-failure-title" className="text-[15px] font-semibold text-fg">
                {subjects(said).length === 1 ? "That change wasn’t saved" : "Some changes weren’t saved"}
              </h2>
              <p className="mt-0.5 text-[12px] text-text-muted">
                We couldn’t save {subjectList(said)}. It’s still on this screen, and nothing has been
                thrown away — but nobody else can see it yet.
              </p>
            </div>
          </div>
          {/* Closes to the bar, never to nothing. Labelled for what it does. */}
          <button
            onClick={dismiss}
            aria-label="Dismiss and keep working"
            data-testid="write-failure-dismiss"
            className="text-text-dim transition-colors hover:text-text"
          >
            <X size={16} />
          </button>
        </div>

        <p className="text-[12px] text-text-muted">{diagnosis(said)}</p>

        {/* NO CODES TO COPY DOWN. This used to print the reference in 15px mono
            as the biggest thing in the box, on the reasoning that a photograph of
            it is the whole bug report. It is — but it asked the wrong person to
            file it, and it read like a crash screen at the exact moment somebody
            needs to be told they are fine. One refused click can be seven rows (a
            week changing its number writes five scripts and two schedule rows),
            so at its worst it was seven hyphenated codes and an instruction to
            photograph them.

            Nothing is lost by dropping it: recordWriteFailure beacons the ref,
            the kind, the id, the op, the reason and the status to
            /api/client-error the instant the write is refused, and the server
            logs the lot. We already have everything the photograph carried,
            filed under a code the person never has to see. What is left here is
            the only part that was ever theirs: their work is safe, and here is
            the button. */}
        <div
          data-testid="write-failure-reassurance"
          className="mt-3 rounded-lg border border-hairline bg-chrome px-3 py-2.5 text-[11.5px] leading-snug text-text-muted"
        >
          Nothing you have done is lost, and there is nothing for you to write down — the
          team has already been sent the details.
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            onClick={dismiss}
            className="px-3 py-2 text-[12px] text-text-muted transition-colors hover:text-text"
          >
            Keep working
          </button>
          <button
            onClick={retry}
            disabled={retrying}
            data-testid="write-failure-retry"
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-2 text-[12px] font-semibold text-onfill transition-colors hover:bg-accent-soft disabled:opacity-50"
          >
            <RefreshCw size={12} className={retrying ? "animate-spin" : undefined} />
            {retrying ? "Trying…" : "Try again"}
          </button>
        </div>
      </div>
    </DialogFrame>
  );
}
